import { Flex, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { CriteriaProjectFreeTextChangeForm } from '@/module/Laboratory/criteria/components/CriteriaProjectFreeTextChangeForm';
import { CriteriaHeaderButtons } from '@/module/Laboratory/criteria/components/components/CriteriaHeaderButtons';
import { useSaveCriteriaData } from '@/module/Laboratory/criteria/components/hooks/useSaveCriteriaData';
import { FreeText, getCenterReportCriteriaDataOptions } from '@/module/ResearchCenter';

import { NoContentPlaceholder, PendingSpinner } from '@/core/components';

import { Id } from '@feature/utils';

import { CriteriaFreeTextCard } from './components/components/CriteriaFreeTextCard';
import { CriteriaItemDeleteButton } from './components/components/CriteriaItemDeleteButton';

export interface CenterReportFreeTextProps {
  labId: Id;
  criteriaCode: string;
}

export function CenterReportFreeText({ labId, criteriaCode }: CenterReportFreeTextProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation();
  const {
    data: { completed, freeTexts },
  } = useSuspenseQuery(getCenterReportCriteriaDataOptions(labId, criteriaCode));
  const { onSubmit, isPending } = useSaveCriteriaData(labId, criteriaCode, close);

  const onChange = (text: FreeText) => onSubmit({ freeText: text });
  const onRemove = (text: FreeText) => {
    if (!text.id) return;
    onSubmit({ toRemove: { id: text.id } });
  };

  return (
    <div>
      <CriteriaHeaderButtons
        completed={completed}
        criteriaCode={criteriaCode}
        labId={labId}
        onAdd={open}
      />
      {opened && (
        <CriteriaProjectFreeTextChangeForm
          defaultValues={{ text: '' }}
          onChange={onChange}
          isSubmitting={isPending}
          onCancel={close}
        />
      )}
      <>{isPending && <PendingSpinner />}</>
      {!isPending && (
        <>
          {freeTexts.length > 0 && (
            <Stack>
              {freeTexts.map((freeText, index) => (
                <Flex
                  align="center"
                  key={freeText.id}
                >
                  <CriteriaFreeTextCard
                    index={index}
                    freeText={freeText}
                    onChange={onChange}
                    isPending={isPending}
                    completed={completed}
                  />

                  {!completed && <CriteriaItemDeleteButton onRemove={() => onRemove(freeText)} />}
                </Flex>
              ))}
            </Stack>
          )}
          {freeTexts.length === 0 && <NoContentPlaceholder title={t('centerReport.noCriteriaContent')} />}
        </>
      )}
    </div>
  );
}
